import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from '../utils/auth';
import { 
  getAllergyStats, 
  getHealthGoalsStats, 
  getFitnessLevelStats,
  supabase 
} from '../utils/supabase';

const AdminStats = () => {
  const navigate = useNavigate();
  const { isAdminUser, loading: authLoading, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    newLast7Days: 0,
    newLast30Days: 0,
    allergies: {},
    healthGoals: {},
    fitnessLevels: {},
  });

  // Redirect if not admin
  useEffect(() => {
    if (!authLoading && !isAdminUser) {
      navigate('/admin-login');
    }
  }, [isAdminUser, authLoading, navigate]);

  // Load stats
  useEffect(() => {
    const loadStats = async () => {
      if (!isAdminUser) return;

      try {
        // Get member stats from view
        const { data: memberStats } = await supabase
          .from('member_stats')
          .select('*')
          .single();

        // Get allergy stats
        const allergyResult = await getAllergyStats();
        
        // Get health goals stats
        const goalsResult = await getHealthGoalsStats();
        
        // Get fitness level stats
        const fitnessResult = await getFitnessLevelStats();

        setStats({
          totalMembers: memberStats?.total_members || 0,
          activeMembers: memberStats?.active_members || 0,
          newLast7Days: memberStats?.new_members_last_7_days || 0,
          newLast30Days: memberStats?.new_members_last_30_days || 0,
          allergies: allergyResult.success ? allergyResult.data : {},
          healthGoals: goalsResult.success ? goalsResult.data : {},
          fitnessLevels: fitnessResult.success ? fitnessResult.data : {},
        });
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [isAdminUser]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin-login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin stats...</p>
        </div>
      </div>
    );
  }

  if (!isAdminUser) {
    return null;
  }

  // Sort and get top items
  const topAllergies = Object.entries(stats.allergies)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);
  
  const topGoals = Object.entries(stats.healthGoals)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);
  
  const fitnessDistribution = Object.entries(stats.fitnessLevels)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pt-20 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Admin Dashboard</h1>
            <p className="text-slate-600">Welcome back, {user?.email}</p>
          </div>
          <button
            onClick={handleSignOut}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Sign Out
          </button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Members"
            value={stats.totalMembers}
            icon="👥"
            color="blue"
          />
          <StatCard
            title="Active Members"
            value={stats.activeMembers}
            icon="✅"
            color="green"
          />
          <StatCard
            title="New (7 days)"
            value={stats.newLast7Days}
            icon="🆕"
            color="purple"
          />
          <StatCard
            title="New (30 days)"
            value={stats.newLast30Days}
            icon="📈"
            color="orange"
          />
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Allergies */}
          <StatsPanel
            title="Top Food Allergies & Sensitivities"
            icon="🥜"
            data={topAllergies}
            emptyMessage="No allergy data yet"
          />

          {/* Health Goals */}
          <StatsPanel
            title="Top Health Goals"
            icon="🎯"
            data={topGoals}
            emptyMessage="No health goals data yet"
          />
        </div>

        {/* Fitness Levels */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3">💪</span>
            <h2 className="text-2xl font-bold text-slate-800">Fitness Level Distribution</h2>
          </div>
          {fitnessDistribution.length > 0 ? (
            <div className="space-y-3">
              {fitnessDistribution.map(([level, count]) => {
                const percentage = stats.totalMembers > 0 
                  ? ((count / stats.totalMembers) * 100).toFixed(1) 
                  : 0;
                return (
                  <div key={level}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700 capitalize">{level}</span>
                      <span className="text-sm text-slate-600">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No fitness level data yet</p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Admin Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl hover:shadow-md transition-all text-left"
            >
              <div className="text-2xl mb-2">✏️</div>
              <div className="font-medium text-slate-800">Content Manager</div>
              <div className="text-sm text-slate-600">Add recipes, workouts & resources</div>
            </button>
            <button
              onClick={() => window.open('https://supabase.com/dashboard', '_blank')}
              className="p-4 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 transition-colors text-left"
            >
              <div className="text-2xl mb-2">🗄️</div>
              <div className="font-medium text-slate-800">Supabase Dashboard</div>
              <div className="text-sm text-slate-600">View database & users</div>
            </button>
            <button
              onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
              className="p-4 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 transition-colors text-left"
            >
              <div className="text-2xl mb-2">💳</div>
              <div className="font-medium text-slate-800">Stripe Dashboard</div>
              <div className="text-sm text-slate-600">View payments & subscriptions</div>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors text-left"
            >
              <div className="text-2xl mb-2">🏠</div>
              <div className="font-medium text-slate-800">Member Dashboard</div>
              <div className="text-sm text-slate-600">View as member</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200"
    >
      <div className={`w-12 h-12 bg-gradient-to-br ${colorClasses[color]} rounded-xl flex items-center justify-center mb-4`}>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-3xl font-bold text-slate-800 mb-1">{value}</div>
      <div className="text-sm text-slate-600">{title}</div>
    </motion.div>
  );
};

// Stats Panel Component
const StatsPanel = ({ title, icon, data, emptyMessage }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
      <div className="flex items-center mb-4">
        <span className="text-3xl mr-3">{icon}</span>
        <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
      </div>
      {data.length > 0 ? (
        <div className="space-y-2">
          {data.map(([item, count], index) => (
            <div key={item} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center">
                <span className="text-sm font-medium text-slate-500 mr-3">#{index + 1}</span>
                <span className="text-slate-800 font-medium">{item}</span>
              </div>
              <span className="text-slate-600 font-semibold">{count}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">{emptyMessage}</p>
      )}
    </div>
  );
};

export default AdminStats;
